import React from 'react'
import { MdVerified } from 'react-icons/md'
import { FaQuoteLeft, FaTrashAlt } from 'react-icons/fa'
import StarDisplay from './StarDisplay'

// Single review item. Used on:
//   - Product detail (Məhsul rəyi)
//   - Shop page    (Satıcı rəyi)
//   - Home + /reviews (Platforma rəyi)
//
// Safe fallbacks (no fake data):
//   - name missing → "ItemZo istifadəçisi"
//   - avatar missing → initial-letter chip with deterministic color
//   - date missing → date row hidden entirely
//
// Props:
//   review: { _id?, name?, avatar?, rating, review, date?, verified? }
//   tone: 'platform' | 'product' | 'seller'  (optional eyebrow chip)
//   canDelete, onDelete, deleting (optional self-delete UI)

const AVATAR_COLORS = ['#7c3aed','#8b5cf6','#a855f7','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6']

const colorForName = (name = '') => {
    const s = String(name) || 'ItemZo'
    const ch = s.charCodeAt(0) || 73
    return AVATAR_COLORS[ch % AVATAR_COLORS.length]
}

const initialOf = (name = '') => {
    const s = String(name).trim()
    if (!s) return 'I'
    return s.charAt(0).toUpperCase()
}

const TONE_LABEL = {
    platform: 'Platforma rəyi',
    product:  'Məhsul rəyi',
    seller:   'Satıcı rəyi',
}

const TONE_CHIP = {
    platform: 'bg-brand-50 text-brand-700',
    product:  'bg-success-50 text-success-700',
    seller:   'bg-accent-50 text-accent-700',
}

const ReviewCard = ({
    review = {},
    tone,
    canDelete = false,
    onDelete,
    deleting = false,
    showQuote = true,
    className = '',
}) => {
    const displayName = review.name && String(review.name).trim()
        ? review.name
        : 'ItemZo istifadəçisi'
    const date = review.date
    const verified = review.verified === true
    const text = review.review || ''
    const rating = Number(review.rating) || 0

    return (
        <article className={`relative rounded-card border border-slate-200 bg-white p-4 shadow-card transition-shadow hover:shadow-card-hover ${className}`}>
            {tone && (
                <span className={`absolute right-3 top-3 inline-flex items-center rounded-pill px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${TONE_CHIP[tone] || TONE_CHIP.platform}`}>
                    {TONE_LABEL[tone] || TONE_LABEL.platform}
                </span>
            )}

            {canDelete && (
                <button
                    type='button'
                    onClick={onDelete}
                    disabled={deleting}
                    aria-label='Rəyi sil'
                    title='Rəyi sil'
                    className='absolute right-3 bottom-3 inline-flex h-7 w-7 items-center justify-center rounded-pill border border-slate-200 bg-white text-slate-400 hover:border-danger-500 hover:text-danger-600 disabled:opacity-60 transition-colors'
                >
                    {deleting ? '…' : <FaTrashAlt size={11} />}
                </button>
            )}

            <header className='flex items-start gap-3'>
                <div
                    className='shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-white font-bold text-sm overflow-hidden'
                    style={{ background: review.avatar ? 'transparent' : colorForName(displayName) }}
                    aria-hidden='true'
                >
                    {review.avatar
                        ? <img src={review.avatar} alt='' className='h-full w-full object-cover' />
                        : <span>{initialOf(displayName)}</span>
                    }
                </div>
                <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-1'>
                        <p className='truncate text-sm font-bold text-slate-800'>{displayName}</p>
                        {verified && (
                            <MdVerified
                                size={14}
                                className='shrink-0 text-brand-500'
                                aria-label='Doğrulanmış'
                            />
                        )}
                    </div>
                    {date && (
                        <p className='text-[11px] text-slate-400'>{date}</p>
                    )}
                </div>
                <StarDisplay value={rating} size={13} />
            </header>

            {showQuote && (
                <FaQuoteLeft className='mt-3 text-slate-200' size={14} aria-hidden='true' />
            )}

            <p className='mt-1.5 text-sm leading-relaxed text-slate-700 whitespace-pre-line break-words'>
                {text}
            </p>
        </article>
    )
}

export default ReviewCard
